import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Chip, CircularProgress, Grid, Icon, TextField } from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import { addDays } from 'date-fns';
// Components
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import ErrorMessage from '../../../components/error-message';
import { AutoFill } from '../../../components/field-autofill';
// Modules
import { actions, selectors } from '../../../redux/reducers/driver-communication';
import { selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations/module';
import { DriverCommunicationDTO, DriverCommunicationLocation } from '../../../redux/reducers/api-types';
// Ressources
import { getGeoAutocompleteURL } from '../../../utils/core-api';
import withGlobalStyles from '../../../utils/global-styles';
import { clsx } from '../../../utils/htmlUtils';
import UserUtils from '../../../utils/userUtils';
import { paths } from '../../paths';

// A communication stays visible to the drivers one week by default
const DEFAULT_DURATION_DAYS = 7;

interface FormState {
    title: string;
    description: string;
    location: DriverCommunicationLocation | null;
    startDate: Date | null;
    endDate: Date | null;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const nowToTheMinute = (): Date => {
    const now = new Date();
    now.setSeconds(0, 0);
    return now;
};

const defaultForm = (): FormState => {
    const startDate = nowToTheMinute();
    return {
        title: '',
        description: '',
        location: null,
        startDate,
        endDate: addDays(startDate, DEFAULT_DURATION_DAYS),
    };
};

interface DriverCommunicationFormPageProps {
    globalClasses: any;
}

const DriverCommunicationFormPage = ({ globalClasses }: DriverCommunicationFormPageProps): JSX.Element => {
    const { t } = useTranslation(['driver_communication', 'common']);
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const isEditMode = !!id;

    const communication = useSelector((state) => selectors.communicationById(state, id));
    const loading = useSelector(pendingSelectors.isLoadingDriverCommunication);
    const saving = useSelector(pendingSelectors.isSavingDriverCommunication);
    const loadError = useSelector(errorSelectors.getDriverCommunicationError);
    const saveError = useSelector(errorSelectors.cudDriverCommunicationError);

    const [form, setForm] = useState<FormState>(defaultForm);
    const [errors, setErrors] = useState<FormErrors>({});
    // Until the OPS picks an end date, it follows the start date
    const [endDateTouched, setEndDateTouched] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    const initializedRef = useRef<boolean>(false);
    const locationFieldRef = useRef<any>(null);
    const successDialogRef = useRef<any>(null);
    const deleteDialogRef = useRef<any>(null);

    useEffect(() => {
        if (isEditMode) {
            dispatch(actions.getDriverCommunication(id));
        }
    }, [dispatch, isEditMode, id]);

    // Fill the form once, when the communication to edit is known
    useEffect(() => {
        if (isEditMode && communication && !initializedRef.current) {
            initializedRef.current = true;
            setForm({
                title: communication.title,
                description: communication.description,
                location: communication.location,
                startDate: new Date(communication.start_date),
                endDate: new Date(communication.end_date),
            });
            setEndDateTouched(true);
        }
    }, [isEditMode, communication]);

    const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const onStartDateChange = (date: Date | null): void => {
        setForm((f) => ({
            ...f,
            startDate: date,
            endDate: !endDateTouched && date ? addDays(date, DEFAULT_DURATION_DAYS) : f.endDate,
        }));
        setErrors((e) => ({ ...e, startDate: undefined, endDate: undefined }));
    };

    const onEndDateChange = (date: Date | null): void => {
        setEndDateTouched(true);
        update('endDate', date);
    };

    // Trim on leaving the field, so the OPS sees what the drivers will get
    const trimField = (key: 'title' | 'description') => (): void => setForm((f) => ({ ...f, [key]: f[key].trim() }));

    const isValidDate = (date: Date | null): boolean => !!date && !isNaN(date.getTime());

    // Every required field is filled: the save button tells it by turning green
    const isComplete =
        !!form.title.trim() &&
        !!form.description.trim() &&
        isValidDate(form.startDate) &&
        isValidDate(form.endDate) &&
        form.endDate > form.startDate;

    const clearLocation = (): void => {
        locationFieldRef.current?.reset();
        update('location', null);
    };

    const validate = (): FormErrors => {
        const found: FormErrors = {};
        if (!form.title.trim()) {
            found.title = t('errors.title_required');
        }
        if (!form.description.trim()) {
            found.description = t('errors.description_required');
        }
        if (!isValidDate(form.startDate)) {
            found.startDate = t('errors.start_date_required');
        }
        if (!isValidDate(form.endDate)) {
            found.endDate = t('errors.end_date_required');
        } else if (!found.startDate && form.endDate <= form.startDate) {
            found.endDate = t('errors.end_before_start');
        }
        return found;
    };

    const showSuccess = (message: string) => () => {
        setSuccessMessage(message);
        successDialogRef.current?.showDialog();
    };

    const onSubmit = (): void => {
        const found = validate();
        setErrors(found);
        if (Object.keys(found).length > 0) {
            return;
        }

        const dto: DriverCommunicationDTO = {
            title: form.title.trim(),
            description: form.description.trim(),
            location: form.location && { ...form.location, address: form.location.address.trim() },
            // UTC, the backend stores it as is
            start_date: form.startDate.toISOString(),
            end_date: form.endDate.toISOString(),
        };

        if (isEditMode) {
            dispatch(actions.updateDriverCommunication(id, dto, showSuccess(t('update_success'))));
        } else {
            dispatch(actions.createDriverCommunication(dto, showSuccess(t('create_success'))));
        }
    };

    const onDeleteConfirm = (): void => {
        deleteDialogRef.current?.closeDialog();
        dispatch(actions.deleteDriverCommunication(id, showSuccess(t('delete_success'))));
    };

    const backToList = (): void => {
        successDialogRef.current?.closeDialog();
        history.push(paths.driverCommunications);
    };

    // Editing a communication that does not exist (anymore): only the error is worth showing
    const notFound = isEditMode && !loading && !communication && !!loadError;

    return (
        <App
            hasRole={UserUtils.hasRoleOpsOrGod()}
            loading={isEditMode && loading && !initializedRef.current}
            header={{ title: t(isEditMode ? 'title_edit' : 'title_add') }}
        >
            <ErrorMessage payload={loadError} />
            <ErrorMessage payload={saveError} />
            {!notFound && (
                <div className={globalClasses.block} style={{ padding: '20px 28px' }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('fields.title')}
                                    value={form.title}
                                    onChange={(e) => update('title', e.target.value)}
                                    onBlur={trimField('title')}
                                    error={!!errors.title}
                                    helperText={errors.title}
                                    inputProps={{ maxLength: 255 }}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label={t('fields.description')}
                                    value={form.description}
                                    onChange={(e) => update('description', e.target.value)}
                                    onBlur={trimField('description')}
                                    error={!!errors.description}
                                    helperText={errors.description}
                                    required
                                    fullWidth
                                    multiline
                                    minRows={4}
                                />
                            </Grid>

                            {/* Location (optional) */}
                            <Grid item xs={12}>
                                <AutoFill
                                    formControl={false}
                                    onRef={(ref) => (locationFieldRef.current = ref)}
                                    datas={{
                                        id: 'location',
                                        name: 'location',
                                        label: t('fields.location'),
                                        required: false,
                                        location: true,
                                        minChar: 3,
                                        style: { width: '100%' },
                                        onChange: (_id: any, item: any) => {
                                            if (item?.location) {
                                                update('location', {
                                                    address: (item.address?.text || item.name || '').trim(),
                                                    lat: item.location[0] ?? item.location.lat,
                                                    lng: item.location[1] ?? item.location.lng,
                                                });
                                            }
                                        },
                                        api: getGeoAutocompleteURL(),
                                    }}
                                />
                                {form.location && (
                                    <Chip
                                        icon={<Icon fontSize="small">place</Icon>}
                                        label={form.location.address}
                                        onDelete={clearLocation}
                                        title={t('fields.location_clear')}
                                        style={{ marginTop: 8, maxWidth: '100%' }}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DateTimePicker
                                    label={t('fields.start_date')}
                                    value={form.startDate}
                                    onChange={onStartDateChange}
                                    format="dd/MM/yyyy HH:mm"
                                    ampm={false}
                                    error={!!errors.startDate}
                                    helperText={errors.startDate}
                                    required
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DateTimePicker
                                    label={t('fields.end_date')}
                                    value={form.endDate}
                                    onChange={onEndDateChange}
                                    format="dd/MM/yyyy HH:mm"
                                    ampm={false}
                                    minDate={form.startDate || undefined}
                                    error={!!errors.endDate}
                                    helperText={errors.endDate}
                                    required
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Icon>arrow_back</Icon>}
                                    onClick={() => history.push(paths.driverCommunications)}
                                    className={globalClasses.roundButton}
                                >
                                    {t('common:back')}
                                </Button>
                                <span style={{ flex: 1 }} />
                                {isEditMode && (
                                    <Button
                                        variant="contained"
                                        startIcon={<Icon>delete_forever</Icon>}
                                        disabled={saving}
                                        onClick={() => deleteDialogRef.current?.showDialog()}
                                        className={clsx(globalClasses.roundButton, globalClasses.btnDanger)}
                                    >
                                        {t('common:delete')}
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    startIcon={
                                        saving ? <CircularProgress size={16} color="inherit" /> : <Icon>save</Icon>
                                    }
                                    disabled={saving}
                                    onClick={onSubmit}
                                    className={clsx(globalClasses.roundButton, isComplete && globalClasses.btnSuccess)}
                                >
                                    {t('common:save')}
                                </Button>
                            </Grid>
                        </Grid>
                    </MuiPickersUtilsProvider>
                </div>
            )}
            <Dialog
                onRef={(ref) => (successDialogRef.current = ref)}
                dialogTitle={t('common:success')}
                description={successMessage}
                btnOKText={t('common:ok')}
                onOK={backToList}
                noKOButton
            />
            <Dialog
                onRef={(ref) => (deleteDialogRef.current = ref)}
                dialogTitle={t('common:delete')}
                description={t('delete_confirm')}
                btnOKText={t('common:delete')}
                btnKOText={t('common:cancel')}
                onOK={onDeleteConfirm}
                onKO={() => deleteDialogRef.current?.closeDialog()}
            />
        </App>
    );
};

export default withGlobalStyles(DriverCommunicationFormPage);
